/**
 * Type for backend currency field - can be string or object
 */
export type BackendCurrency = string | { code?: string } | undefined | null;

/**
 * Safely extracts currency code from backend data
 * Handles both string and object formats
 *
 * @param currency - Can be string ("USD") or object ({ code: "USD", ... })
 * @returns Currency code string (e.g., "USD", "INR")
 */
export function getCurrencyCode(currency: BackendCurrency): string {
  if (!currency) return 'USD'; // Default

  if (typeof currency === 'string') {
    return currency; // Already a string
  }

  if (typeof currency === 'object' && currency.code) {
    return currency.code; // Extract from object
  }

  return 'USD'; // Fallback
}

/**
 * Converts an amount between currencies using a rates map.
 *
 * @param amount - numeric amount in the source currency
 * @param fromCurrency - ISO currency code of the source amount
 * @param toCurrency - ISO currency code to convert into
 * @param exchangeRates - map of exchange rates relative to baseCurrency (e.g. USD)
 * @param baseCurrency - the base currency for the rates map (default 'USD')
 * @returns converted amount (or original amount when conversion data unavailable)
 */
export function convertAmountWithRates({
  amount,
  fromCurrency,
  toCurrency,
  exchangeRates,
  baseCurrency = 'USD',
}: {
  amount: number;
  fromCurrency: string | null | undefined;
  toCurrency: string | null | undefined;
  exchangeRates?: Record<string, number> | null;
  baseCurrency?: string;
}): number {
  const safeAmount =
    typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0;
  const from = (fromCurrency || baseCurrency || 'USD').toUpperCase();
  const to = (toCurrency || baseCurrency || 'USD').toUpperCase();

  if (safeAmount === 0) return 0;
  if (from === to) return safeAmount;

  const base = (baseCurrency || 'USD').toUpperCase();
  const rates = exchangeRates || {};

  const getRate = (code: string) => {
    if (code === base) return 1;
    const rate = rates[code];
    return typeof rate === 'number' && !Number.isNaN(rate) && rate > 0
      ? rate
      : undefined;
  };

  let amountInBase = safeAmount;

  if (from !== base) {
    const fromRate = getRate(from);
    if (!fromRate) {
      return safeAmount;
    }
    amountInBase = safeAmount / fromRate;
  }

  if (to === base) {
    return amountInBase;
  }

  const toRate = getRate(to);
  if (!toRate) {
    return safeAmount;
  }

  return amountInBase * toRate;
}
